// Hero.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Search as SearchIcon,
  X,
  List
} from "lucide-react";
import "../Styles/Hero.css";

/* ----- Simple double-month calendar component ----- */
const Calendar = ({ startDate, endDate, onSelect, closeOnSelect = false }) => {
  // startDate/endDate are Date or null
  const today = new Date();
  const [baseMonth, setBaseMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  useEffect(() => {
    // ensure baseMonth shows relevant month if a selection exists
    if (startDate) setBaseMonth(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  }, [startDate]);

  const addMonth = (m) => {
    setBaseMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + m, 1));
  };

  const buildMonth = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0..6
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let dayCounter = 1 - firstDay;
    while (dayCounter <= daysInMonth) {
      const week = [];
      for (let i = 0; i < 7; i++, dayCounter++) {
        const d = new Date(year, month, dayCounter);
        week.push(d);
      }
      weeks.push(week);
    }
    return weeks;
  };

  const inRange = (d) => {
    if (!startDate) return false;
    if (!endDate) return d.toDateString() === startDate.toDateString();
    return d >= startDate && d <= endDate;
  };

  const isDisabled = (d) => {
    // disable days before today
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < t || d.getMonth() !== baseMonth.getMonth() && d.getMonth() !== (baseMonth.getMonth() + 1) % 12 && d.getMonth() !== (baseMonth.getMonth() + 1);
  };

  const handleDateClick = (d) => {
    if (d < new Date(today.getFullYear(), today.getMonth(), today.getDate())) return;
    onSelect(d);
    if (closeOnSelect) {
      // parent handles visibility
    }
  };

  const month1 = baseMonth;
  const month2 = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 1, 1);

  const renderMonth = (monthDate) => {
    const weeks = buildMonth(monthDate);
    const monthLabel = monthDate.toLocaleString(undefined, { month: "long", year: "numeric" });
    return (
      <div className="cal-month" key={monthLabel}>
        <div className="cal-header">{monthLabel}</div>
        <div className="cal-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div className="cal-weekday" key={d}>{d}</div>
          ))}

          {weeks.map((week, wi) =>
            week.map((d, idx) => {
              const isThisMonth = d.getMonth() === monthDate.getMonth();
              const disabled = d < new Date(today.getFullYear(), today.getMonth(), today.getDate()) || !isThisMonth;
              const selected = inRange(d);
              return (
                <button
                  key={`${wi}-${idx}`}
                  className={`cal-day ${!isThisMonth ? "muted" : ""} ${disabled ? "disabled" : ""} ${selected ? "selected" : ""}`}
                  onClick={() => handleDateClick(new Date(d.getFullYear(), d.getMonth(), d.getDate()))}
                  disabled={disabled}
                >
                  {d.getDate()}
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="calendar-double">
      <div className="cal-actions">
        <button className="cal-btn" onClick={() => addMonth(-1)}>&lt;</button>
        <button className="cal-btn" onClick={() => addMonth(1)}>&gt;</button>
      </div>
      <div className="cal-two">
        {renderMonth(month1)}
        {renderMonth(month2)}
      </div>
    </div>
  );
};

/* ----- Main Hero Component ----- */
const Hero = () => {
  const [location, setLocation] = useState("");
  const [guests, setGuests] = useState(1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarTarget, setCalendarTarget] = useState(null); // 'in' or 'out'
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [amenities, setAmenities] = useState({
    ac: false,
    pool: false,
    longterm: false,
  });
  const [isSticky, setIsSticky] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const suggestions = ["Lekki Phase 1", "Victoria Island", "Ikoyi", "Ikeja GRA", "Banana Island", "Apapa", "Yaba"];

  const heroRef = useRef(null);
  const searchRef = useRef(null);


  // Disable page scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [mobileOpen]);


  useEffect(() => {
    const onScroll = () => {
      if (!heroRef.current) return;
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      // when hero bottom is <= 80, pin search as sticky small bar
      setIsSticky(heroBottom <= 80);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // handle calendar select logic (select check-in then check-out)
  const handleCalendarSelect = (date) => {
    if (!checkIn || (checkIn && checkOut)) {
      // start new range
      setCheckIn(date);
      setCheckOut(null);
    } else {
      // have checkIn only -> set checkOut (if after), otherwise swap
      if (date >= checkIn) {
        setCheckOut(date);
        setShowCalendar(false);
        setCalendarTarget(null);
      } else {
        // earlier than checkIn -> make it new checkIn
        setCheckIn(date);
      }
    }
  };

  const toggleAmenity = (key) => {
    setAmenities((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onSearch = () => {
    // implement actual search / route as needed
    const payload = {
      location,
      checkIn: checkIn ? checkIn.toISOString().slice(0, 10) : null,
      checkOut: checkOut ? checkOut.toISOString().slice(0, 10) : null,
      guests,
      amenities,
    };
    console.log("Search payload:", payload);
    alert("Search triggered — check console for payload.");
  };

  // convenience display helpers
  const formatDate = (d) => (d ? d.toLocaleDateString() : "");
  const rangeLabel = checkIn ? (checkOut ? `${formatDate(checkIn)} → ${formatDate(checkOut)}` : `${formatDate(checkIn)} →`) : "";

  return (
    <section className="hero-section" ref={heroRef}>
      <div className="hero-overlay" />

      <motion.div className="hero-content" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="hero-title">Find Your Perfect Stay</h1>
        <p className="hero-subtitle">Luxury short-let apartments & serviced homes</p>

        {/* Floating search; when sticky is true we show compact variant */}
        <div className={`search-wrap ${isSticky ? "sticky" : ""}`} ref={searchRef}>
          {/* Desktop / main search */}
          <div className="search-container">
            <div className="field location-field">
              <label>Location</label>
              <div className="input-with-icon">
                <MapPin size={16} />
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Where are you going?"
                />
              </div>

              <AnimatePresence>
                {showSuggestions && (
                  <motion.div className="suggestions-box" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}>
                    {suggestions.filter(s => s.toLowerCase().includes(location.toLowerCase())).map((s, i) => (
                      <div key={i} className="suggestion-item" onMouseDown={() => setLocation(s)}>
                        {s}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="field date-field">
              <label>Check In</label>
              <div className="input-with-icon" onClick={() => { setShowCalendar(true); setCalendarTarget("in"); }}>
                <CalendarIcon size={16} />
                <input readOnly value={checkIn ? checkIn.toLocaleDateString() : ""} placeholder="Select date" />
              </div>
            </div>

            <div className="field date-field">
              <label>Check Out</label>
              <div className="input-with-icon" onClick={() => { setShowCalendar(true); setCalendarTarget("out"); }}>
                <CalendarIcon size={16} />
                <input readOnly value={checkOut ? checkOut.toLocaleDateString() : ""} placeholder="Select date" />
              </div>
            </div>

            <div className="field guests-field">
              <label>Guests</label>
              <div className="input-with-icon">
                <Users size={16} />
                <input type="number" min="1" value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value || 1)))} />
              </div>
            </div>

            <button className="search-btn" onClick={onSearch}>
              <SearchIcon size={16} /> <span className="search-text">Search</span>
            </button>
          </div>

          {/* Calendar popup anchored to the search area */}
          <AnimatePresence>
            {showCalendar && (
              <motion.div className="calendar-popup-wrapper" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}>
                <Calendar
                  startDate={checkIn}
                  endDate={checkOut}
                  onSelect={(d) => {
                    if (!checkIn || (checkIn && checkOut)) {
                      setCheckIn(d);
                      setCheckOut(null);
                      // remain open so user can pick checkout
                    } else {
                      if (d >= checkIn) {
                        setCheckOut(d);
                        setShowCalendar(false);
                        setCalendarTarget(null);
                      } else {
                        setCheckIn(d);
                        setCheckOut(null);
                      }
                    }
                  }}
                />
                <div className="calendar-actions">
                  <button className="cal-clear" onClick={() => { setCheckIn(null); setCheckOut(null); }}>Clear</button>
                  <button className="cal-done" onClick={() => setShowCalendar(false)}>Done</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Amenities pills */}
          <div className="amenities-row">
            <button className={`pill ${amenities.ac ? "active" : ""}`} onClick={() => toggleAmenity("ac")}>Air Conditioning</button>
            <button className={`pill ${amenities.pool ? "active" : ""}`} onClick={() => toggleAmenity("pool")}>Swimming Pool</button>
            <button className={`pill ${amenities.longterm ? "active" : ""}`} onClick={() => toggleAmenity("longterm")}>Long Term Stays</button>
          </div>
        </div>

        {/* Mobile slide-up drawer trigger */}
        <div className="mobile-search-trigger">
          <button className="mobile-open-btn" onClick={() => setMobileOpen(true)}>
            <List size={16} /> Quick search
          </button>
        </div>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div className="mobile-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 0.45 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} />
              <motion.div className="mobile-drawer" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 300 }}>
                <div className="mobile-drawer-header">
                  <div className="logo-small">GoldenStays</div>
                  <X size={20} onClick={() => setMobileOpen(false)} />
                </div>

                <div className="mobile-drawer-body">
                  <div className="mobile-field">
                    <label>Location</label>
                    <div className="input-with-icon">
                      <MapPin size={16} />
                      <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Where are you going?" />
                    </div>
                  </div>

                  <div className="mobile-row">
                    <div className="mobile-field">
                      <label>Check In</label>
                      <div className="input-with-icon" onClick={() => { setShowCalendar(true); }}>
                        <CalendarIcon size={16} />
                        <input readOnly value={checkIn ? checkIn.toLocaleDateString() : ""} placeholder="Select" />
                      </div>
                    </div>

                    <div className="mobile-field">
                      <label>Check Out</label>
                      <div className="input-with-icon" onClick={() => { setShowCalendar(true); }}>
                        <CalendarIcon size={16} />
                        <input readOnly value={checkOut ? checkOut.toLocaleDateString() : ""} placeholder="Select" />
                      </div>
                    </div>
                  </div>

                  <div className="mobile-field">
                    <label>Guests</label>
                    <div className="input-with-icon">
                      <Users size={16} />
                      <input type="number" min="1" value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value || 1)))} />
                    </div>
                  </div>

                  <div className="mobile-amenities">
                    <button className={`pill ${amenities.ac ? "active" : ""}`} onClick={() => toggleAmenity("ac")}>Air Conditioning</button>
                    <button className={`pill ${amenities.pool ? "active" : ""}`} onClick={() => toggleAmenity("pool")}>Swimming Pool</button>
                    <button className={`pill ${amenities.longterm ? "active" : ""}`} onClick={() => toggleAmenity("longterm")}>Long Term Stays</button>
                  </div>

                  <div className="mobile-actions">
                    <button className="search-btn" onClick={() => { onSearch(); setMobileOpen(false); }}>
                      <SearchIcon size={16} /> Search
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default Hero;
